import { injectCookieConsentStyles } from './styles';
import { createSbjsBlocker, type ScriptSourcePattern } from './sbjs-blocker';
import { loadCmp, readCachedScriptPatterns } from './loader';

// Styles before any async work so banner DOM never paints unstyled.
injectCookieConsentStyles();

const script =
  (document.currentScript as HTMLScriptElement | null) ||
  document.querySelector<HTMLScriptElement>('script[data-site-id]');

const siteId = script?.getAttribute('data-site-id')?.trim() ?? '';

/**
 * Site-specific tag sources, read straight off the embed tag.
 *
 * These have to be known at tick 0. The configuration fetch takes a network
 * round-trip, and a first-party proxied Google tag is usually already in the
 * <head> and running by the time it lands — so a config-only source would let
 * it through on every first visit, which is exactly the visit where consent
 * matters most.
 *
 * Format: "/mbw3/:analytics,/xyz/:advertisement" — category defaults to
 * analytics when omitted.
 */
function parseScriptPatternsAttribute(value: string | null | undefined): ScriptSourcePattern[] {
  if (!value) return [];

  const out: ScriptSourcePattern[] = [];

  for (const chunk of value.split(',')) {
    const trimmed = chunk.trim();
    if (!trimmed) continue;

    const sep = trimmed.lastIndexOf(':');
    const pattern = sep === -1 ? trimmed : trimmed.slice(0, sep).trim();
    const rawCategory = sep === -1 ? 'analytics' : trimmed.slice(sep + 1).trim();

    if (
      rawCategory !== 'analytics' &&
      rawCategory !== 'advertisement' &&
      rawCategory !== 'functionality'
    ) {
      continue;
    }

    if (pattern) out.push({ pattern, category: rawCategory });
  }

  return out;
}

// Cookie/script gate from the first tick (analytics + advertisement + functionality denied).
// Unlocked per-category when CookieConsent reports acceptance.
const trackingBlocker = createSbjsBlocker();

const attributePatterns = parseScriptPatternsAttribute(script?.getAttribute('data-tag-paths'));
const cachedPatterns = siteId ? readCachedScriptPatterns(siteId) : [];

if (attributePatterns.length > 0 || cachedPatterns.length > 0) {
  // Attribute wins: it is authoritative and cannot be stale.
  trackingBlocker.setScriptPatterns(
    attributePatterns.length > 0 ? attributePatterns : cachedPatterns,
  );
}

trackingBlocker.syncConsent({ analytics: false, advertisement: false, functionality: false });

if (!siteId) {
  console.error(
    '[Yargı CMP] Missing data-site-id on the client.js <script> tag. CMP loader aborted.',
  );
} else {
  const apiBaseUrl = script?.getAttribute('data-api-base')?.trim() ?? '';
  void loadCmp(siteId, apiBaseUrl, {
    onTrackingConsentChange(state) {
      trackingBlocker.syncConsent(state);
    },
    onScriptPatterns(patterns) {
      // Only top up when the embed tag did not already state them.
      if (attributePatterns.length === 0 && patterns.length > 0) {
        trackingBlocker.setScriptPatterns(patterns);
      }
    },
  }).catch((err: unknown) => {
    console.error('[Yargı CMP] Loader failed:', err);
  });
}
